import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';

@Pipe({
    name: 'safeHtml',
    standalone: false
})
export class SafeHtmlPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';
    
    // First check if this appears to be plain text rather than HTML
    const isPlainText = !/<\/?[a-z][\s\S]*>/i.test(value);
    
    let processedValue = value;
    
    if (isPlainText) {
      // This is plain text, format it with paragraphs
      processedValue = this.formatPlainText(processedValue);
    } else {
      // This is HTML, process it normally
      processedValue = this.formatHtml(processedValue);
    }
    
    // Wrap the content in a div with proper styling
    const wrappedContent = `<div style="
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12pt;
      line-height: 1.5;
      color: #000;
      text-align: left;
      margin: 0;
      padding: 0;
    ">${processedValue}</div>`;
    
    return this.sanitizer.bypassSecurityTrustHtml(wrappedContent);
  }
  
  private formatPlainText(text: string): string {
    // Normalize line endings
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // First, extract the title (first line)
    const lines = normalizedText.split('\n');
    let title = '';
    let contentLines = lines;
    
    if (lines.length > 0) {
      title = lines[0].trim();
      contentLines = lines.slice(1);
    }
    
    // Format the title
    let result = '';
    if (title) {
      result += `<h1 style="font-size: 24px !important; font-weight: 900 !important; margin-top: 0; margin-bottom: 16px; color: #000 !important; text-align: center;">${title}</h1>`;
      result += '<hr style="border: none; border-top: 1px solid #ddd; margin: 10px 0 20px 0;">';
    }
    
    // Process the rest of the content
    let currentParagraph = '';
    let inList = false;
    let paragraphStart = true; // Track if we're at the start of a new paragraph
    
    for (let i = 0; i < contentLines.length; i++) {
      const line = contentLines[i];
      const trimmedLine = line.trim();
      
      // Check for empty lines - these always end paragraphs and lists
      if (trimmedLine === '') {
        // Empty line, end the current paragraph if there is one
        if (currentParagraph) {
          result += `<p style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">${currentParagraph}</p>`;
          currentParagraph = '';
          paragraphStart = true;
        }
        
        // Also close any open list
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        continue;
      }
      
      // Check if this line looks like a heading (ends with colon or is all caps)
      if (paragraphStart && (trimmedLine.endsWith(':') || (trimmedLine.toUpperCase() === trimmedLine && trimmedLine.length > 3 && !/^\d+\./.test(trimmedLine)))) {
        // This looks like a heading, end current paragraph if any
        if (currentParagraph) {
          result += `<p style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">${currentParagraph}</p>`;
          currentParagraph = '';
        }
        
        // Close any open list
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        
        // Add as a heading with stronger font-weight
        result += `<h3 style="font-size: 18px !important; font-weight: 900 !important; margin-top: 20px; margin-bottom: 15px; color: #000 !important; text-align: left;">${trimmedLine}</h3>`;
        paragraphStart = true;
      } 
      // Check if this line starts with a bullet point or number
      else if (/^[\-\*\•]|\d+[\.\)]/.test(trimmedLine)) {
        // Start a list if not already in one
        if (!inList) {
          // End current paragraph if any
          if (currentParagraph) {
            result += `<p style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">${currentParagraph}</p>`;
            currentParagraph = '';
          }
          result += '<ul style="list-style-type: none; margin-left: 0; padding-left: 0; margin-top: 15px; margin-bottom: 15px;">';
          inList = true;
        }
        
        // Add as list item, removing the bullet character
        const content = trimmedLine.replace(/^[\-\*\•\s]+|\d+[\.\)]\s+/, '');
        result += `<li style="position: relative; padding-left: 20px; margin-bottom: 10px; line-height: 1.5; color: #000; text-indent: 0;"><span style="display: inline-block;">${content}</span></li>`;
        paragraphStart = false;
      } 
      // Check if this is a continuation of a bullet point
      else if (inList && !paragraphStart && i > 0 && /^[\-\*\•]|\d+[\.\)]/.test(contentLines[i-1].trim())) {
        // Find the last </li> tag and replace it
        const lastLiIndex = result.lastIndexOf('</li>');
        if (lastLiIndex !== -1) {
          // Remove the closing </li> tag
          result = result.substring(0, lastLiIndex);
          // Add the current line with proper spacing
          result += ` ${trimmedLine}</span></li>`;
        }
      }
      // Handle lines that should start new paragraphs
      else if (paragraphStart || this.shouldStartNewParagraph(line, i, contentLines)) {
        // End current paragraph if any
        if (currentParagraph) {
          result += `<p style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">${currentParagraph}</p>`;
          currentParagraph = '';
        }
        
        // Close any open list
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        
        // Start a new paragraph with this line
        currentParagraph = trimmedLine;
        paragraphStart = false;
      } 
      // Otherwise, this is a continuation of the current paragraph
      else {
        // Add to current paragraph with a space
        if (currentParagraph) {
          currentParagraph += ' ' + trimmedLine;
        } else {
          currentParagraph = trimmedLine;
          paragraphStart = false;
        }
      }
    }
    
    // Close any open elements
    if (currentParagraph) {
      result += `<p style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">${currentParagraph}</p>`;
    }
    
    if (inList) {
      result += '</ul>';
    }
    
    // Format special text patterns like [company name]
    result = result.replace(/\[(.*?)\]/g, '<strong style="font-weight: bold; color: #000;">[$1]</strong>');
    
    return result;
  }
  
  private shouldStartNewParagraph(line: string, index: number, lines: string[]): boolean {
    const trimmedLine = line.trim();
    
    // Check for common paragraph starters
    if (/^(We|Our|You|Your|The|This|These|Those|It|If|When|Why|How|What|Where|Who|Safeguarding|Securing|Links|PRO TIP)/i.test(trimmedLine)) {
      return true;
    }
    
    // Check if line starts with a capital letter and previous line ended with period
    if (index > 0) {
      const prevLine = lines[index-1].trim();
      if (prevLine.endsWith('.') && /^[A-Z]/.test(trimmedLine)) {
        return true;
      }
    }
    
    // Check if this is a very short line (likely a heading or new section)
    if (trimmedLine.length < 40 && !trimmedLine.endsWith(',')) {
      return true;
    }
    
    return false;
  }
  
  private formatHtml(html: string): string {
    // Add classes to placeholders
    let processedValue = html
      .replace(/\[COMPANY_INFORMATION\]/g, '<span class="company-info">[COMPANY_INFORMATION]</span>')
      .replace(/\[COMPANY_COUNTRY\]/g, '<span class="company-country">[COMPANY_COUNTRY]</span>')
      .replace(/\[DATE\]/g, '<span class="date-placeholder">[DATE]</span>')
      
      // Clean up any PDF content divs and styles
      .replace(/<div class="pdf-content">[\s\S]*?<\/style>/g, '')
      .replace(/<\/div>\s*$/g, '')
      
      // Format headings with proper styling
      .replace(/<h1(.*?)>(.*?)<\/h1>/gi, '<h1$1 style="font-size: 24px !important; font-weight: 900 !important; margin-top: 0; margin-bottom: 16px; color: #000 !important; text-align: center;">$2</h1>')
      .replace(/<h2(.*?)>(.*?)<\/h2>/gi, '<h2$1 style="font-size: 20px !important; font-weight: 900 !important; margin-top: 25px; margin-bottom: 15px; color: #000 !important; text-align: left;">$2</h2>')
      .replace(/<h3(.*?)>(.*?)<\/h3>/gi, '<h3$1 style="font-size: 18px !important; font-weight: 900 !important; margin-top: 20px; margin-bottom: 15px; color: #000 !important; text-align: left;">$2</h3>')
      .replace(/<h4(.*?)>(.*?)<\/h4>/gi, '<h4$1 style="font-size: 16px !important; font-weight: 900 !important; margin-top: 15px; margin-bottom: 10px; color: #000 !important; text-align: left;">$2</h4>')
      
      // Format paragraphs
      .replace(/<p(.*?)>(.*?)<\/p>/gi, '<p$1 style="margin-bottom: 10px; line-height: 1.5; text-align: left; color: #000;">$2</p>')
      
      // Format lists and list items - ensure they start on new lines
      .replace(/<ul>([\s\S]*?)<\/ul>/gi, function(match, content) {
        // Process each list item to ensure proper formatting
        const formattedContent = content.replace(/<li(.*?)>([\s\S]*?)<\/li>/gi, function(liMatch, liAttrs, liContent) {
          // Clean up any whitespace at the beginning of list items
          const cleanContent = liContent.trim();
          return `<li${liAttrs} style="position: relative; padding-left: 20px; margin-bottom: 10px; line-height: 1.5; color: #000; text-indent: 0;"><span style="display: inline-block;">${cleanContent}</span></li>`;
        });
        
        return '<ul style="list-style-type: none; margin-left: 0; padding-left: 0; margin-top: 15px; margin-bottom: 15px;">' + 
               formattedContent + 
               '</ul>';
      })
      
      // Format text styling elements
      .replace(/<strong(.*?)>(.*?)<\/strong>/gi, '<strong$1 style="font-weight: bold; color: #000;">$2</strong>')
      .replace(/<b(.*?)>(.*?)<\/b>/gi, '<b$1 style="font-weight: bold; color: #000;">$2</b>')
      .replace(/<em(.*?)>(.*?)<\/em>/gi, '<em$1 style="font-style: italic;">$2</em>')
      .replace(/<i(.*?)>(.*?)<\/i>/gi, '<i$1 style="font-style: italic;">$2</i>')
      
      // Handle spans with font-weight
      .replace(/<span style="([^"S]*font-weight:\s*bold[^"S]*)">(.*?)<\/span>/gi, '<span style="$1 font-weight: bold; color: #000;">$2</span>')
      .replace(/<span style="([^"S]*font-weight:\s*700[^"S]*)">(.*?)<\/span>/gi, '<span style="$1 font-weight: bold; color: #000;">$2</span>')
      .replace(/<span style="([^"S]*font-weight:\s*600[^"S]*)">(.*?)<\/span>/gi, '<span style="$1 font-weight: bold; color: #000;">$2</span>');
    
    // Remove any leading empty paragraphs
    processedValue = processedValue.replace(/^<p[^>]*>([\s\n]*)<\/p>/, '');
    
    // Add horizontal line after title if not present
    if (processedValue.includes('<h1') || processedValue.includes('<h2')) {
      const firstHeadingEndIndex = Math.max(
        processedValue.indexOf('</h1>') > -1 ? processedValue.indexOf('</h1>') : -1,
        processedValue.indexOf('</h2>') > -1 ? processedValue.indexOf('</h2>') : -1
      );
      
      if (firstHeadingEndIndex > -1) {
        const before = processedValue.substring(0, firstHeadingEndIndex + 5); // +5 for </h1> or </h2>
        const after = processedValue.substring(firstHeadingEndIndex + 5);
        processedValue = before + '<hr style="border: none; border-top: 1px solid #ddd; margin: 10px 0 20px 0;">' + after;
      }
    }
    
    return processedValue;
  }
}

@Pipe({
    name: 'safe',
    standalone: false
})
export class SafeUrlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}
  
  transform(url: string): SafeResourceUrl {
    if (!url) return this.sanitizer.bypassSecurityTrustResourceUrl('');
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
